import React from "react";

interface School {
  id: string;
  name: string;
}

interface SchoolSelectorProps {
  schools: School[];
  schoolsLoading: boolean;
  selectedSchoolIds: string[];
  onSchoolIdsChange: (schoolIds: string[]) => void;
}

const SchoolSelector: React.FC<SchoolSelectorProps> = ({
  schools,
  schoolsLoading,
  selectedSchoolIds,
  onSchoolIdsChange,
}) => {
  const handleSelectAll = () => {
    const allSchoolIds = schools.map((school) => school.id);
    onSchoolIdsChange(allSchoolIds);
  };

  const handleDeselectAll = () => {
    onSchoolIdsChange([]);
  };

  const handleSchoolToggle = (schoolId: string) => {
    const newSelectedIds = selectedSchoolIds.includes(schoolId)
      ? selectedSchoolIds.filter((id) => id !== schoolId)
      : [...selectedSchoolIds, schoolId];
    onSchoolIdsChange(newSelectedIds);
  };

  if (schoolsLoading) {
    return (
      <div className="mb-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-4 bg-gray-200 rounded w-full"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-3">
        <label className="block text-sm font-medium text-gray-700">
          Filter by Schools
        </label>
        <div className="space-x-2">
          <button
            onClick={handleSelectAll}
            className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Select All
          </button>
          <button
            onClick={handleDeselectAll}
            className="text-xs px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
          >
            Deselect All
          </button>
        </div>
      </div>

      <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3 bg-white">
        {schools.length === 0 ? (
          <p className="text-gray-500 text-sm">No schools available</p>
        ) : (
          <div className="space-y-2 grid grid-cols-2 gap-2">
            {schools.map((school) => (
              <label
                key={school.id}
                className="flex items-center space-x-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedSchoolIds.includes(school.id)}
                  onChange={() => handleSchoolToggle(school.id)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{school.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {selectedSchoolIds.length > 0 && (
        <p className="text-xs text-gray-500 mt-2">
          {selectedSchoolIds.length} school(s) selected
        </p>
      )}
    </div>
  );
};

export default SchoolSelector;
